import React, { useState, useEffect, useMemo } from "react";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftAvatar from "components/SoftAvatar";
import swal from "sweetalert2";
import bruce from "assets/images/bruce-mars.jpg";
import { api } from "utils/Api";
import EditFormPopup from "layouts/admin/user/data/EditForm";
import DeleteForm from "layouts/admin/Organization/DeleteForm";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SmartTableCard from "utils/Smart Table/SmartTableCard";
import TenantAddPropertyPopup from "./AdminTenantAddToProperty"; // ← renamed file/component
import { useParams } from "react-router-dom";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import { Box, Tooltip } from "@mui/material";
import AdminTenantAddPropertyPopup from "./AdminTenantAddToProperty";
import { getSecureItem } from "utils/SecureStorage";
import RemoveFromParentForm from "layouts/admin/Organization/MultipleDeleteForm";
import PropTypes from "prop-types";
import { showSuccess } from "utils/messageAlerts";
import { canUpdate } from "utils/permissionManager";
import { canDelete } from "utils/permissionManager";
import { canCreate } from "utils/permissionManager";
import { useAuth } from "context/AuthContext";
import RestoreIcon from "@mui/icons-material/Restore";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import MultiConfirmDialog from "utils/MultiConfirmDialog";
import { useHierarchy } from "context/HierarchyContext";

function AdminTenant({ propertyId }) {
  const [tenants, setTenants] = useState([]);
  const [units, setUnits] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isTenantAddPopupOpen, setIsTenantAddPopupOpen] = useState(false);
  const [popupMode, setPopupMode] = useState("create");
  const [selectedTenant, setSelectedTenant] = useState([]);
  const [deletePopupOpen, setDeletePopupOpen] = useState(false);
  const [unitFilterAnchor, setUnitFilterAnchor] = useState(null);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [unitSearch, setUnitSearch] = useState("");
  const roleId = JSON.parse(getSecureItem("currentUser"))?.role_id;
  const { isAdmin, isManager } = useAuth();
  const { orgId } = useHierarchy();
  const currentOrgId = JSON.parse(getSecureItem("currentUser"))?.organization_id;
  const current_user_role_id = getSecureItem("role_id");
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: null,
    tenant: null,
    unit: null,
  });

  useEffect(() => {
    fetchTenants();
    fetchUnits();
  }, []);

  const fetchUnits = async () => {
    try {
      const unitData = await api.unit.getUnits();
      setUnits(unitData);
    } catch (e) {
      // Error fetching properties
    }
  };

  // Fetch all users, then filter only role_id === 5 (Tenants)
  const fetchTenants = async () => {
    const role_id = 5; // ← update this to your actual Tenant role_id
    try {
      const userData = await api.user.getFiltertedUser({
        role_id,
        organization_id: orgId || currentOrgId,
        property_id: parseInt(propertyId),
        current_user_role_id,
      });
      
      console.log('userData: ', userData);
      const formatted = userData.map((u) => ({
        id: u.id,
        username: u.username,
        first_name: u.first_name,
        last_name: u.last_name,
        email: u.email,
        phone_number: u.phone_number,
        description: u.description,
        active: u.active,
        image: u.image || null,
        organization_id: u.organization_id,
        role_id: u.role_id,
        createdAt: u.created_at,
        property_id: u.property_id,
        unit_id: u.unit_id,
        user_unit: u.user_unit || null,
      }));

      setTenants(formatted);
    } catch (e) {
      setTenants([]);
    }
  };

  // Edit Tenant
  const handleEditTenant = (tenant) => {
    setPopupMode("edit");
    setSelectedTenant(tenant);
    setIsPopupOpen(true);
  };

  // Delete Tenant
  const handleDeleteTenant = async (payload) => {
    try {
      await api.userUnit.deleteUserUnit(payload);

      showSuccess("Tenant Soft Removed from Unit Successfully.");

      fetchTenants();
    } catch (err) {}
  };

  // Search Tenants
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const unitMap = useMemo(() => {
    const map = {};
    units.forEach((u) => {
      map[u.id] = u.name;
    });
    return map;
  }, [units]);

  const filteredTenants = tenants.filter((tenant) => {
    const term = searchTerm.toLowerCase();
    
    const matchesSearch =
    !searchTerm.trim() ||
    tenant.first_name?.toLowerCase().includes(term) ||
    tenant.last_name?.toLowerCase().includes(term) ||
    tenant.email?.toLowerCase().includes(term) ||
    tenant.username?.toLowerCase().includes(term);
    
    const matchesUnit =
    selectedUnits.length === 0 ||
    tenant.user_unit?.some((uu) => selectedUnits.includes(uu.unit_id));
    return matchesSearch && matchesUnit;
  });
  
  const filteredUnitList = units
  .filter((prop) => prop.property_id === Number(propertyId))
  .filter((prop) => prop.name.toLowerCase().includes(unitSearch.toLowerCase()));
  
  
  const canEdit = canUpdate("User Unit");
  const canRemove = canDelete("User Unit");
  const showActionsColumn = canEdit || canRemove;

  const handleTenantActionClick = (tenant, action) => {
    setConfirmDialog({
      open: true,
      type: action,
      tenant,
    });
  };

  const handleConfirmAction = async (unit_id) => {
    const { tenant, type } = confirmDialog;

    if (!unit_id) return;

    const payload = {
      unit_id,
      user_id: tenant.id,
      user_role_id: 5,
      organization_id: orgId || currentOrgId,
      property_id: propertyId,
    };

    try {
      if (type === "delete") {
        await api.userUnit.deleteUserUnitHard(payload);
        showSuccess("Tenant permanently deleted successfully");
      } else {
        await api.userUnit.restoreUserUnit({
          unit_id,
          user_id: tenant.id,
          organization_id: orgId,
          current_user_role_id: roleId,
        });
        showSuccess("Tenant restored successfully");
      }

      fetchTenants();
    } catch (e) {}

    setConfirmDialog({ open: false, type: null, tenant: null, unit: null });
  };

  return (
    <SmartTableCard
      title={`All Residents`}
      headerRight={[
        canCreate("User Unit") && (
          <SoftButton
            key="add-btn"
            variant="outlined"
            color="info"
            size="small"
            onClick={() => setIsTenantAddPopupOpen(true)}
          >
            Add Residents to Unit
          </SoftButton>
        ),

        <SoftInput
          key="search-input"
          placeholder="Search Residents"
          value={searchTerm}
          onChange={handleSearch}
          size="small"
        />,
      ]}
      columns={[
        { label: "Username", key: "Username", align: "center" },
        { label: "Email", key: "Email", align: "center" },
        { label: "Phone", key: "Phone", align: "center" },
        { label: "Description", key: "Description", align: "center" },
        {
          label: (
            <SoftBox display="flex" alignItems="center" justifyContent="center" gap="4px">
              <span>Unit</span>

              <SoftBox position="relative" display="flex" alignItems="center">
                <FilterAltOutlinedIcon
                  fontSize="small"
                  sx={{
                    cursor: "pointer",
                    color: selectedUnits.length ? "#0284C7" : "#475467",
                    "&:hover": { color: "#0284C7" },
                  }}
                  onClick={(e) => setUnitFilterAnchor(e.currentTarget)}
                />

                {selectedUnits.length > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -4,
                      right: -6,
                      background: "#0284C7",
                      color: "#fff",
                      borderRadius: "50%",
                      fontSize: 10,
                      width: 16,
                      height: 16,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {selectedUnits.length}
                  </Box>
                )}
              </SoftBox>
            </SoftBox>
          ),
          key: "UnitID",
          align: "center",
        },
        ...(isAdmin || isManager ? [{ label: "Active", key: "Active", align: "center" }] : []),
        ...(showActionsColumn ? [{ label: "Actions", key: "Actions", align: "center" }] : []),
      ]}
      rows={filteredTenants.map((tenant) => {
        const validUserProperties = (tenant.user_unit || []).filter(
          (uu) => unitMap?.[uu.unit_id]
        );

        const isActive = validUserProperties.some((uu) => uu.active === true);
        const isInactive = !isActive;

        return {
          Username: tenant.username,
          Email: tenant.email,
          Phone: tenant.phone_number,
          Description: tenant.description,
          UnitID: (() => {
            if (!tenant.user_unit?.length) return "N/A";

            const unitNames = tenant.user_unit
              .filter((uu) => uu.user_id === tenant.id)
              .map((uu) => unitMap[uu.unit_id])
              .filter(Boolean);

            return unitNames.length ? unitNames.join(", ") : "N/A";
          })(),
          ...(isAdmin || isManager
            ? {
                Active: (() => {
                  const inactiveUnit = tenant.user_unit?.find((uu) => uu.active === false);
                  const activeUnit = tenant.user_unit?.find((uu) => uu.active === true);

                  // ❌ If any inactive → show actions
                  if (inactiveUnit) {
                    return (
                      <SoftBox display="flex" justifyContent="center" gap="6px">
                        {/* Restore */}
                        <Tooltip title="Restore Tenant" arrow>
                          <IconButton
                            onClick={() =>
                              setConfirmDialog({
                                open: true,
                                type: "restore",
                                tenant,
                                unit: inactiveUnit, // ✅ ONLY ONE UNIT
                              })
                            }
                            sx={{ color: "#16A34A" }}
                          >
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {/* Delete */}
                        <Tooltip title="Delete Permanently" arrow>
                          <IconButton
                            onClick={() =>
                              setConfirmDialog({
                                open: true,
                                type: "delete",
                                tenant,
                                unit: inactiveUnit, // ✅ ONLY ONE UNIT
                              })
                            }
                            sx={{ color: "#DC2626" }}
                          >
                            <DeleteForeverIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </SoftBox>
                    );
                  }

                  // ✅ Otherwise show active
                  if (activeUnit) {
                    return (
                      <SoftTypography variant="button" color="success" fontWeight="bold">
                        Yes
                      </SoftTypography>
                    );
                  }

                  return "N/A";
                })(),
              }
            : {}),
          ...(showActionsColumn && {
            Actions: (
              <SoftBox display="flex" justifyContent="center" gap="8px">
                {canEdit && (
                  <IconButton
                    onClick={() => handleEditTenant(tenant)}
                    disabled={isInactive}
                    sx={{
                      border: "1px solid #D0D5DD",
                      backgroundColor: "#F9FAFB",
                      width: 34,
                      height: 34,
                      "& svg": { fontSize: 18 },
                      color: "#475467",
                      "&:hover": {
                        backgroundColor: "#E0F2FE",
                        borderColor: "#38BDF8",
                        color: "#0284C7",
                      },
                    }}
                  >
                    <EditIcon fontSize="sm" />
                  </IconButton>
                )}

                {canRemove && (
                  <IconButton
                    onClick={() => {
                      setDeletePopupOpen(true);
                      setSelectedTenant(tenant);
                    }}
                    disabled={isInactive}
                    sx={{
                      border: "1px solid #FECACA",
                      backgroundColor: "#FEF2F2",
                      width: 34,
                      height: 34,
                      "& svg": { fontSize: 18 },
                      color: "#B91C1C",
                      "&:hover": {
                        backgroundColor: "#FEE2E2",
                        borderColor: "#F87171",
                        color: "#991B1B",
                      },
                    }}
                  >
                    <DeleteIcon fontSize="sm" />
                  </IconButton>
                )}
              </SoftBox>
            ),
          }),
        };
      })}
    >
      {/* {filteredTenants.length === 0 && (
        <div style={{ margin: "15px", fontSize: "17px", textAlign: "center" }}>
          No tenants found
        </div>
      )} */}

      <MultiConfirmDialog
        open={confirmDialog.open}
        type={confirmDialog.type}
        title={confirmDialog.type === "delete" ? "Delete Tenant" : "Restore Tenant"}
        description={
          confirmDialog.type === "delete"
            ? "This will permanently delete the tenant. This action cannot be undone."
            : "Are you sure you want to restore this tenant?"
        }
        confirmText={confirmDialog.type === "delete" ? "Delete Permanently" : "Restore"}
        onClose={() => setConfirmDialog({ open: false, type: null, tenant: null, unit: null })}
        onConfirm={handleConfirmAction}
        assignments={confirmDialog.tenant?.user_unit || []}
        parentMap={unitMap}
        idKey="unit_id"
        entityLabel="Unit"
      />

      {isPopupOpen && (
        <EditFormPopup
          mode={popupMode}
          user={selectedTenant}
          onClose={() => {
            setIsPopupOpen(false);
            fetchTenants();
          }}
        />
      )}

      {isTenantAddPopupOpen && (
        <AdminTenantAddPropertyPopup
          onClose={() => setIsTenantAddPopupOpen(false)}
          refreshData={fetchTenants}
          orgId={orgId}
        />
      )}

      {/* {deletePopupOpen && (
        <DeleteForm
          data={selectedTenant}
          onClose={() => setDeletePopupOpen(false)}
          onDelete={(id) => {
            handleDeleteTenant(id);
            setDeletePopupOpen(false);
          }}
        />
      )} */}

      {deletePopupOpen && (
        <RemoveFromParentForm
          open={deletePopupOpen}
          entity={selectedTenant}
          entityLabel="Tenant"
          parentLabel="Unit"
          assignments={selectedTenant?.user_unit}
          parentKey="unit_id"
          parentMap={unitMap}
          onClose={() => setDeletePopupOpen(false)}
          onRemove={(unit_id, user) => {
            handleDeleteTenant({
              unit_id,
              property_id: propertyId,
              user_id: user.id,
              user_role_id: 5,
              organization_id: orgId,
            });
            setDeletePopupOpen(false);
          }}
        />
      )}

      <Menu
        anchorEl={unitFilterAnchor}
        open={Boolean(unitFilterAnchor)}
        onClose={() => setUnitFilterAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        {/* Search */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={1}
          py={1}
          gap={1}
        >
          <SoftInput
            placeholder="Search units"
            size="small"
            value={unitSearch}
            onChange={(e) => setUnitSearch(e.target.value)}
          />

          <IconButton
            size="small"
            onClick={() => {
              setUnitFilterAnchor(null);
              setUnitSearch(""); // optional: reset search
            }}
          >
            ✕
          </IconButton>
        </Box>

        {/* Clear */}
        {selectedUnits.length > 0 && (
          <MenuItem onClick={() => setSelectedUnits([])} sx={{ color: "#0284C7", fontWeight: 600 }}>
            Clear Selection
          </MenuItem>
        )}

        {/* Property List */}
        {filteredUnitList.length > 0 ? (
          filteredUnitList.map((prop) => (
            <MenuItem
              key={prop.id}
              onClick={() => {
                if (selectedUnits.includes(prop.id)) {
                  setSelectedUnits(selectedUnits.filter((id) => id !== prop.id));
                } else {
                  setSelectedUnits([...selectedUnits, prop.id]);
                }
              }}
            >
              <Checkbox checked={selectedUnits.includes(prop.id)} />
              <ListItemText primary={prop.name} primaryTypographyProps={{ variant: "body3" }} />
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>No units found</MenuItem>
        )}
      </Menu>
    </SmartTableCard>
  );
}

AdminTenant.propTypes = {
  propertyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default AdminTenant;